import React, { useState } from 'react';
import { portalAPI } from '../services/api';

const CreatePortalCard = ({ onPortalCreated }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    description: ''
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const response = await portalAPI.createPersonalPortal(formData);
      setIsModalOpen(false);
      setFormData({ name: '', url: '', description: '' });
      if (onPortalCreated && response?.portal) {
        // The API returns the created portal in the 'portal' field of the response
        onPortalCreated(response.portal);
      }
    } catch (err) {
      setError(err.message || 'Failed to create portal');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <>
      <div
        onClick={() => setIsModalOpen(true)}
        className="bg-white rounded-2xl border-2 border-dashed border-gray-200 p-5 cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/10 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col items-center justify-center h-48 shadow-[0_8px_30px_rgb(0,0,0,0.02)]"
      >
        <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center mb-3 text-indigo-600 shadow-inner group-hover:bg-indigo-100 transition-colors">
          <svg
            className="w-6 h-6 text-indigo-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth={2.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 4v16m8-8H4"
            />
          </svg>
        </div>
        <h3 className="font-bold text-gray-800 text-base mb-1 tracking-tight text-center">
          Create Portal
        </h3>
        <p className="text-xs text-gray-400 text-center">
          Add a new personal shortcut
        </p>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 transition-opacity duration-300">
          <div className="bg-white rounded-2xl border border-gray-100 w-full max-w-md p-6 shadow-2xl relative transform transition-all duration-300">
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-lg font-bold text-gray-800 tracking-tight">Create New Portal</h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-1.5 rounded-full transition-all"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {error && (
              <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-3 rounded-r-xl">
                <p className="text-xs font-medium text-red-700">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                  Portal Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="block w-full rounded-xl border-gray-200 px-4 py-2.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500 transition-colors"
                  placeholder="e.g., My Personal Tool"
                />
              </div>

              <div>
                <label htmlFor="url" className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                  Portal URL *
                </label>
                <input
                  type="url"
                  id="url"
                  name="url"
                  required
                  value={formData.url}
                  onChange={handleChange}
                  className="block w-full rounded-xl border-gray-200 px-4 py-2.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500 transition-colors"
                  placeholder="https://example.com"
                />
              </div>

              <div>
                <label htmlFor="description" className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="3"
                  className="block w-full rounded-xl border-gray-200 px-4 py-2.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500 transition-colors"
                  placeholder="A short description for your portal"
                />
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-gray-50 mt-6">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-sm font-semibold text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-xl transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`px-4 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-md shadow-indigo-100 transition-all ${
                    isSubmitting ? 'opacity-75 cursor-not-allowed' : ''
                  }`}
                >
                  {isSubmitting ? 'Creating...' : 'Create Portal'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default CreatePortalCard;
